import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";

import { CredentialsAlert } from "@/components/CredentialsAlert";
import { Icon } from "@/components/Icon";
import { Modal } from "@/components/Modal";
import { Toggle } from "@/components/Toggle";
import {
  useClients,
  useCreateClient,
  useDeleteClient,
  useToggleClient,
  useUpdateClient,
} from "@/hooks/useClients";
import { extractApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import type { Client } from "@/types/api";

const _USERNAME_REGEX = /^[A-Za-z0-9_\-.]+$/;

const createSchema = z.object({
  name: z.string().min(1).max(120),
  username: z
    .string()
    .min(3, "Mínimo 3 caracteres")
    .max(64)
    .regex(_USERNAME_REGEX, "Solo letras, dígitos, _, -, ."),
  password: z.string().min(4, "Mínimo 4 caracteres").max(128),
});

const editSchema = z.object({
  name: z.string().min(1).max(120),
  username: z
    .string()
    .min(3, "Mínimo 3 caracteres")
    .max(64)
    .regex(_USERNAME_REGEX, "Solo letras, dígitos, _, -, ."),
  password: z
    .string()
    .max(128)
    .refine((v) => v === "" || v.length >= 4, "Mínimo 4 caracteres (o vacío para no cambiar)"),
});

type CreateValues = z.infer<typeof createSchema>;
type EditValues = z.infer<typeof editSchema>;

export function ClientsPage() {
  const { data, isLoading } = useClients();
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState<Client | null>(null);
  const [editClient, setEditClient] = useState<Client | null>(null);
  const [deleteClient, setDeleteClient] = useState<Client | null>(null);

  return (
    <div className="view">
      <div className="view__head">
        <div className="view__title-block">
          <div className="view__eyebrow">
            <span className="tick" /> Consumidores
          </div>
          <h1 className="view__title">Clientes</h1>
          <p className="view__subtitle">
            Consumidores NTRIP (rovers, hincators) que descargan RTCM desde mountpoints.
            La autenticación es por usuario/clave y queda registrada por sesión.
          </p>
        </div>
        <div className="view__actions">
          <button
            className="btn btn--accent"
            onClick={() => {
              setCreated(null);
              setOpen(true);
            }}
          >
            <Icon.Plus />
            Nuevo cliente
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="card card--ticks" style={{ padding: 20 }}>
          <span className="sk" style={{ height: 140 }} />
        </div>
      ) : !data || data.length === 0 ? (
        <div className="card card--ticks">
          <div className="empty">
            <div className="empty__reticle">
              <Icon.Clients style={{ width: 18, height: 18 }} />
            </div>
            <div className="empty__title">Aún no hay clientes</div>
            <div className="empty__sub">
              Crea uno para autorizar un consumidor NTRIP. Luego asigna sus accesos en la pestaña
              Accesos (ACL).
            </div>
          </div>
        </div>
      ) : (
        <div className="card card--ticks">
          <table className="tbl">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Usuario</th>
                <th>Última conexión</th>
                <th>Creado</th>
                <th style={{ textAlign: "right", paddingRight: 24 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.map((c) => (
                <ClientRow
                  key={c.id}
                  client={c}
                  onEdit={() => setEditClient(c)}
                  onDelete={() => setDeleteClient(c)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateClientDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setCreated(null);
        }}
        created={created}
        onCreated={setCreated}
      />

      <EditClientDialog
        client={editClient}
        onClose={() => setEditClient(null)}
      />

      <DeleteClientDialog
        client={deleteClient}
        onClose={() => setDeleteClient(null)}
      />
    </div>
  );
}

function ClientRow({
  client,
  onEdit,
  onDelete,
}: {
  client: Client;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const toggle = useToggleClient();
  return (
    <tr>
      <td style={{ fontWeight: 500 }}>{client.name}</td>
      <td className="tbl__mono">{client.username}</td>
      <td className="tbl__mono tbl__muted">{formatDateTime(client.last_connection_at)}</td>
      <td className="tbl__mono tbl__muted">{formatDateTime(client.created_at)}</td>
      <td style={{ textAlign: "right", paddingRight: 24 }}>
        <div
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          <span className={`badge ${client.is_enabled ? "badge--ok" : "badge--off"}`}>
            {client.is_enabled ? "Activo" : "Inactivo"}
          </span>
          <Toggle
            checked={client.is_enabled}
            disabled={toggle.isPending}
            ariaLabel={`Habilitar ${client.name}`}
            onChange={(checked) =>
              toggle.mutate(
                { id: client.id, enable: checked },
                {
                  onSuccess: () =>
                    toast.success(`${client.name} ${checked ? "habilitado" : "deshabilitado"}`),
                  onError: (e) => toast.error(extractApiError(e)),
                },
              )
            }
          />
          <button
            type="button"
            className="btn btn--ghost"
            title="Editar"
            aria-label="Editar"
            onClick={onEdit}
            style={{ padding: "4px 8px" }}
          >
            <Icon.Edit style={{ width: 16, height: 16 }} />
          </button>
          <button
            type="button"
            className="btn btn--danger"
            title="Eliminar"
            aria-label="Eliminar"
            onClick={onDelete}
            style={{ padding: "4px 8px" }}
          >
            <Icon.Trash style={{ width: 16, height: 16 }} />
          </button>
        </div>
      </td>
    </tr>
  );
}

interface CreateProps {
  open: boolean;
  onClose: () => void;
  created: Client | null;
  onCreated: (c: Client) => void;
}

function CreateClientDialog({ open, onClose, created, onCreated }: CreateProps) {
  const create = useCreateClient();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: "", username: "", password: "" },
  });

  const onSubmit = async (values: CreateValues) => {
    try {
      const c = await create.mutateAsync(values);
      onCreated(c);
      toast.success(`Cliente ${c.name} creado`);
      reset();
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={created ? "Cliente creado" : "Nuevo cliente"}
      description={
        created
          ? "Copia las credenciales antes de cerrar — no se mostrarán de nuevo."
          : "Crea un consumidor NTRIP. Otorga acceso desde la pestaña Accesos."
      }
      footer={
        created ? (
          <button className="btn btn--primary" onClick={onClose}>
            Listo
          </button>
        ) : null
      }
    >
      {created ? (
        <CredentialsAlert
          rows={[
            { label: "Usuario", value: created.username },
            { label: "Contraseña", value: created.password ?? "(no devuelto)" },
          ]}
        />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} style={{ display: "grid", gap: 14 }}>
          <div className="field">
            <label className="field__label" htmlFor="client-name">Nombre</label>
            <input
              id="client-name"
              className="input"
              placeholder="Hincator Field 02"
              {...register("name")}
            />
            {errors.name && <div className="field__error">{errors.name.message}</div>}
          </div>
          <div className="field">
            <label className="field__label" htmlFor="client-username">Usuario</label>
            <input
              id="client-username"
              className="input input--mono"
              placeholder="hincator_field_02"
              autoComplete="off"
              {...register("username")}
            />
            {errors.username && (
              <div className="field__error">{errors.username.message}</div>
            )}
          </div>
          <div className="field">
            <label className="field__label" htmlFor="client-password">Contraseña</label>
            <input
              id="client-password"
              className="input input--mono"
              placeholder="Mínimo 4 caracteres"
              autoComplete="new-password"
              {...register("password")}
            />
            {errors.password && (
              <div className="field__error">{errors.password.message}</div>
            )}
          </div>
          <div className="modal__foot">
            <button
              type="button"
              className="btn"
              onClick={onClose}
              disabled={create.isPending}
            >
              Cancelar
            </button>
            <button type="submit" className="btn btn--accent" disabled={create.isPending}>
              {create.isPending && <span className="spinner" />}
              Crear
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function EditClientDialog({
  client,
  onClose,
}: {
  client: Client | null;
  onClose: () => void;
}) {
  const update = useUpdateClient();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: { name: "", username: "", password: "" },
  });

  useEffect(() => {
    if (!client) return;
    reset({
      name: client.name,
      username: client.username,
      password: "",
    });
  }, [client, reset]);

  if (!client) return null;

  const onSubmit = async (values: EditValues) => {
    const patch: Parameters<typeof update.mutateAsync>[0] = {
      id: client.id,
      name: values.name,
      username: values.username,
    };
    if (values.password) {
      patch.password = values.password;
    }
    try {
      await update.mutateAsync(patch);
      toast.success(`${client.name} actualizado`);
      onClose();
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`Editar ${client.name}`}
      description="Deja la contraseña vacía si no quieres cambiarla."
    >
      <form onSubmit={handleSubmit(onSubmit)} style={{ display: "grid", gap: 14 }}>
        <div className="field">
          <label className="field__label" htmlFor="client-edit-name">Nombre</label>
          <input id="client-edit-name" className="input" {...register("name")} />
          {errors.name && <div className="field__error">{errors.name.message}</div>}
        </div>
        <div className="field">
          <label className="field__label" htmlFor="client-edit-user">Usuario</label>
          <input
            id="client-edit-user"
            className="input input--mono"
            autoComplete="off"
            {...register("username")}
          />
          {errors.username && (
            <div className="field__error">{errors.username.message}</div>
          )}
        </div>
        <div className="field">
          <label className="field__label" htmlFor="client-edit-pass">
            Nueva contraseña <span className="muted">(opcional)</span>
          </label>
          <input
            id="client-edit-pass"
            className="input input--mono"
            placeholder="Dejar vacío para conservar la actual"
            autoComplete="new-password"
            {...register("password")}
          />
          {errors.password && (
            <div className="field__error">{errors.password.message}</div>
          )}
        </div>
        <div className="modal__foot">
          <button type="button" className="btn" onClick={onClose} disabled={update.isPending}>
            Cancelar
          </button>
          <button type="submit" className="btn btn--accent" disabled={update.isPending}>
            {update.isPending && <span className="spinner" />}
            Guardar cambios
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DeleteClientDialog({
  client,
  onClose,
}: {
  client: Client | null;
  onClose: () => void;
}) {
  const del = useDeleteClient();
  if (!client) return null;

  const onConfirm = async () => {
    try {
      await del.mutateAsync(client.id);
      toast.success(`${client.name} eliminado`);
      onClose();
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`Eliminar ${client.name}`}
      description="Esta acción no se puede deshacer. Se borrarán también los accesos (ACL) asociados."
    >
      <div className="modal__foot">
        <button type="button" className="btn" onClick={onClose} disabled={del.isPending}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn--danger"
          onClick={onConfirm}
          disabled={del.isPending}
        >
          {del.isPending && <span className="spinner" />}
          Eliminar definitivamente
        </button>
      </div>
    </Modal>
  );
}
